import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, Download, Filter, BadgeCheck, X, CheckCircle2 } from "lucide-react";
import { logger } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { key: "probation", label: "Probation", color: "bg-amber-100 text-amber-800" },
  { key: "temporary", label: "Temporary", color: "bg-sky-100 text-sky-800" },
  { key: "permanent", label: "Permanent", color: "bg-emerald-100 text-emerald-800" },
];

const ROLE_OPTIONS = [
  "super_admin", "general_manager", "property_admin", "supervisor", "housekeeping", "maintenance",
];

const StatusBadge = ({ status }) => {
  const s = STATUS_OPTIONS.find((x) => x.key === status) || STATUS_OPTIONS[0];
  return <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider ${s.color}`}>{s.label}</span>;
};

const downloadCsv = (rows) => {
  const header = ["Name", "Employee ID", "Email", "Department", "Status", "Role", "Date of joining", "Tenure", "Properties"];
  const csv = [header.join(",")]
    .concat(rows.map((r) => [
      `"${(r.name || "").replace(/"/g, '""')}"`,
      r.employee_id || "",
      r.email || "",
      r.department || "",
      r.employment_status || "",
      r.role || "",
      r.date_of_joining || "",
      `"${r.tenure_label}"`,
      `"${(r.property_names || []).join("; ")}"`,
    ].join(",")))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `team-tenure-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function TeamTenure() {
  const { selectedId, properties } = useProperty();
  const { user } = useAuth();
  const canConfirm = ["super_admin", "general_manager", "property_admin"].includes(user?.role);
  const [departments, setDepartments] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingId, setConfirmingId] = useState(null);
  const [filters, setFilters] = useState({
    property_id: "",
    department: "",
    employment_status: "",
    role: "",
    q: "",
  });

  const loadDepartments = useCallback(async () => {
    try {
      const { data } = await api.get("/departments");
      setDepartments(data);
    } catch (e) {
      logger.error("Department load failed", e);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.property_id) params.set("property_id", filters.property_id);
      if (filters.department) params.set("department", filters.department);
      if (filters.employment_status) params.set("employment_status", filters.employment_status);
      if (filters.role) params.set("role", filters.role);
      if (filters.q) params.set("q", filters.q);
      const { data } = await api.get(`/team-tenure?${params.toString()}`);
      setRows(data);
    } catch (e) {
      logger.error("Team tenure load failed", e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadDepartments(); }, [loadDepartments]);
  useEffect(() => { load(); }, [load]);

  const confirmProbation = async (row) => {
    if (!window.confirm(`Move ${row.name} from Probation to Permanent? They will receive a notification.`)) return;
    setConfirmingId(row.id);
    try {
      await api.put(`/users/${row.id}`, { employment_status: "permanent" });
      toast.success(`${row.name} is now permanent`);
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, employment_status: "permanent" } : r)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to confirm");
    } finally {
      setConfirmingId(null);
    }
  };

  const clearFilters = () => setFilters({ property_id: "", department: "", employment_status: "", role: "", q: "" });
  const activeFilterCount = useMemo(
    () => Object.entries(filters).filter(([k, v]) => v && v.length).length,
    [filters]
  );

  const breakdown = useMemo(() => {
    const total = rows.length;
    const counts = STATUS_OPTIONS.reduce((acc, s) => {
      acc[s.key] = rows.filter((r) => r.employment_status === s.key).length;
      return acc;
    }, {});
    return { total, ...counts };
  }, [rows]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="label-overline">Team</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Team Tenure</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-lg">
            Overall tenure of every team member, their employment status, and department — filterable across the org.
          </p>
        </div>
        <Button onClick={() => downloadCsv(rows)} variant="outline" disabled={!rows.length} data-testid="team-tenure-export">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-zinc-200 rounded-md p-4">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Total</div>
          <div className="font-display text-3xl font-black tracking-tight mt-1">{breakdown.total}</div>
        </div>
        {STATUS_OPTIONS.map((s) => (
          <div key={s.key} className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`tile-${s.key}`}>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">{s.label}</div>
            <div className="font-display text-3xl font-black tracking-tight mt-1">{breakdown[s.key] || 0}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-md p-4" data-testid="filters-bar">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-zinc-500" />
          <span className="text-xs uppercase tracking-widest font-semibold text-zinc-700">Filters</span>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="ml-auto text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1" data-testid="clear-filters">
              <X className="w-3 h-3" /> Clear ({activeFilterCount})
            </button>
          )}
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <Label className="text-[11px] uppercase tracking-widest text-zinc-600">Search</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Name, email or employee ID…"
                className="pl-9 h-10"
                data-testid="filter-q"
              />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-600">Department</Label>
            <Select value={filters.department || "__all"} onValueChange={(v) => setFilters({ ...filters, department: v === "__all" ? "" : v })}>
              <SelectTrigger className="mt-1 h-10" data-testid="filter-department"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All departments</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-600">Status</Label>
            <Select value={filters.employment_status || "__all"} onValueChange={(v) => setFilters({ ...filters, employment_status: v === "__all" ? "" : v })}>
              <SelectTrigger className="mt-1 h-10" data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-600">Role</Label>
            <Select value={filters.role || "__all"} onValueChange={(v) => setFilters({ ...filters, role: v === "__all" ? "" : v })}>
              <SelectTrigger className="mt-1 h-10" data-testid="filter-role"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All roles</SelectItem>
                {ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {properties?.length > 1 && (
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-600">Property</Label>
              <Select value={filters.property_id || "__all"} onValueChange={(v) => setFilters({ ...filters, property_id: v === "__all" ? "" : v })}>
                <SelectTrigger className="mt-1 h-10" data-testid="filter-property"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All properties</SelectItem>
                  {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        {(() => {
          if (loading) {
            return (
              <div className="p-10 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            );
          }
          if (rows.length === 0) {
            return (
              <div className="p-10 text-center" data-testid="team-tenure-empty">
                <BadgeCheck className="w-8 h-8 mx-auto text-zinc-300" strokeWidth={1.5} />
                <div className="mt-2 text-zinc-500 text-sm">No team members match these filters.</div>
              </div>
            );
          }
          return (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="team-tenure-table">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-zinc-600 font-semibold">Member</th>
                    <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-zinc-600 font-semibold">Department</th>
                    <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-zinc-600 font-semibold">Status</th>
                    <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-zinc-600 font-semibold">Role</th>
                    <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-zinc-600 font-semibold">Joined</th>
                    <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-zinc-600 font-semibold">Tenure</th>
                    <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-zinc-600 font-semibold">Properties</th>
                    {canConfirm && <th className="px-4 py-3 text-[11px] uppercase tracking-widest text-zinc-600 font-semibold text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`tenure-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-zinc-900">{r.name}</div>
                        <div className="text-[11px] text-zinc-500">{r.employee_id} · {r.email}</div>
                      </td>
                      <td className="px-4 py-3 text-zinc-700">{r.department || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.employment_status} /></td>
                      <td className="px-4 py-3 text-zinc-700 capitalize">{(r.role || "").replace("_", " ")}</td>
                      <td className="px-4 py-3 text-zinc-700">{r.date_of_joining || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-4 py-3 font-semibold text-zinc-900">{r.tenure_label}</td>
                      <td className="px-4 py-3 text-zinc-700 text-xs">{r.property_names?.join(", ") || <span className="text-zinc-400">—</span>}</td>
                      {canConfirm && (
                        <td className="px-4 py-3 text-right">
                          {r.employment_status === "probation" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => confirmProbation(r)}
                              disabled={confirmingId === r.id}
                              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                              data-testid={`confirm-probation-${r.id}`}
                            >
                              {confirmingId === r.id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirm</>}
                            </Button>
                          ) : (
                            <span className="text-[11px] text-zinc-400">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
